/**
 * Display formatting helpers.
 *
 * Layer rule: these return strings and data, never markup. A theme decides
 * whether a date is grey or bold; this module only decides that 2026-08-15
 * reads as "2026年8月15日" in Chinese and "August 15, 2026" in English.
 *
 * 展示层格式化工具。
 *
 * 分层规则：只返回字符串和数据，不返回标记。日期是灰色还是加粗由主题决定；
 * 本模块只负责让 2026-08-15 在中文下读作「2026年8月15日」、英文下读作
 * "August 15, 2026"。
 */

/**
 * Format an ISO date for display.
 *
 * 把 ISO 日期格式化用于展示。
 *
 * @param {string} iso ISO date string, e.g. '2026-08-15' / ISO 日期串。
 * @param {'zh'|'en'} [lang] Language / 语言。
 * @param {{month?: 'short'|'long'|'numeric'}} [options] Style options / 样式选项。
 * @returns {string} Formatted date, or '' when unparseable / 格式化日期，无法解析时返回空串。
 */
export function formatDate(iso, lang = 'zh', options = {}) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return String(iso)

  if (lang === 'zh') {
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`
  }
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: options.month || 'long',
    day: 'numeric',
  })
}

/**
 * Format just the year and month, for grouped listings.
 *
 * 只格式化年月，用于分组列表。
 *
 * @param {string} iso ISO date string / ISO 日期串。
 * @param {'zh'|'en'} [lang] Language / 语言。
 * @returns {string} Formatted year-month / 格式化的年月。
 */
export function formatYearMonth(iso, lang = 'zh') {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return String(iso)
  if (lang === 'zh') return `${date.getFullYear()}年${date.getMonth() + 1}月`
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
}

/**
 * Split an author string into parts, flagging the ones to emphasise.
 *
 * A group homepage bolds its own members in every citation. Doing that with
 * string replacement would corrupt names that are substrings of others, so we
 * tokenise first and match whole names.
 *
 * 把作者串拆成片段，并标出需要强调的作者。课题组主页会在每条引用里加粗自己人。
 * 用字符串替换实现会破坏「某个名字是另一个名字子串」的情况，因此先分词再整名匹配。
 *
 * @param {string} authors Comma-separated author list / 逗号分隔的作者串。
 * @param {string[]} [highlightNames] Names to emphasise / 需要强调的姓名。
 * @returns {Array<{name: string, highlight: boolean}>} Author tokens / 作者片段。
 */
export function parseAuthors(authors, highlightNames = []) {
  const normalise = (name) =>
    String(name || '')
      .toLowerCase()
      .replace(/[.,]/g, '')
      .replace(/\s+/g, ' ')
      .trim()

  const targets = new Set(highlightNames.map(normalise).filter(Boolean))

  return String(authors || '')
    .split(/[,;]/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((name) => {
      const key = normalise(name)
      // Also match "Rao, Z." against "Zhibo Rao" by comparing surname + initial.
      // 同时让 "Rao, Z." 能匹配 "Zhibo Rao"：比较姓氏与名字首字母。
      const highlight =
        targets.has(key) ||
        [...targets].some((target) => {
          const targetParts = target.split(' ')
          const nameParts = key.split(' ')
          if (targetParts.length < 2 || nameParts.length < 2) return false
          const targetSurname = targetParts[targetParts.length - 1]
          const nameSurname = nameParts[nameParts.length - 1]
          if (targetSurname !== nameSurname) return false
          return targetParts[0][0] === nameParts[0][0]
        })
      return { name, highlight }
    })
}

/**
 * Split a contact string into the address to link and the text to show.
 *
 * People do not type bare addresses into a field labelled "contact email". They
 * type `someone@example.com (Name)`, or `Name <someone@example.com>`, because
 * an address alone does not say who reads it. Feeding that whole string to
 * `mailto:` produces a link that opens a mail client with a malformed
 * recipient — broken in a way nobody notices until a message bounces.
 *
 * So the address is extracted for the href and the original string is kept for
 * display: what was written still reads as written, and the link works.
 *
 * 把联系邮箱串拆成「用于链接的地址」和「用于显示的文字」。
 *
 * 没有人会在一个写着「联系邮箱」的输入框里只填一个裸地址。人们会写
 * `someone@example.com（某某）` 或 `某某 <someone@example.com>`，因为光有地址
 * 说明不了是谁在看。把整串塞进 mailto: 会生成一个收件人格式错误的链接——
 * 这种坏法很隐蔽，直到有封信被退回来才会有人发现。
 *
 * 因此：地址抽出来做 href，原串保留用于显示。写成什么样还读成什么样，链接也是通的。
 *
 * @param {string} value Raw field value / 字段原始值。
 * @returns {{address: string, label: string}} Address for `mailto:` and text to show / 用于 mailto 的地址与显示文字。
 */
export function parseContactEmail(value) {
  const label = String(value || '').trim()
  // Deliberately loose: it only has to find the address inside whatever was
  // typed around it, not decide whether the address is deliverable.
  // 刻意写得宽松：它只需要在人写下的东西里找出地址，不负责判断这个地址能否收信。
  const match = label.match(/[^\s<>()（），,;:]+@[^\s<>()（），,;:]+\.[A-Za-z]{2,}/)
  return { address: match ? match[0] : '', label }
}

/**
 * Render a byte count in human units.
 *
 * 把字节数换算成可读单位。
 *
 * @param {number} bytes Size in bytes / 字节数。
 * @returns {string} e.g. '2.4 MB' / 例如 '2.4 MB'。
 */
export function formatBytes(bytes) {
  const size = Number(bytes) || 0
  if (size < 1024) return `${size} B`
  const units = ['KB', 'MB', 'GB']
  let value = size / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`
}

/**
 * Build a URL-safe slug.
 *
 * Chinese titles have no ASCII to slug from, so a CJK title falls back to a
 * short hash of itself rather than collapsing to an empty string.
 *
 * 生成 URL 安全的 slug。中文标题没有可用的 ASCII，因此 CJK 标题回退成自身的短哈希，
 * 而不是塌缩成空串。
 *
 * @param {string} text Source text / 源文本。
 * @returns {string} Slug / URL 片段。
 */
export function slugify(text) {
  const ascii = String(text || '')
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')

  if (ascii) return ascii.slice(0, 64)

  let hash = 0
  const source = String(text || '')
  for (let i = 0; i < source.length; i += 1) {
    hash = (hash * 31 + source.charCodeAt(i)) >>> 0
  }
  return `p-${hash.toString(36)}`
}

/**
 * Group records into descending year buckets.
 *
 * Publications and awards both read best grouped by year, and both want the
 * newest year first with records inside a year in their own order.
 *
 * 把记录按年份倒序分组。论文和获奖都适合按年份分组展示：年份新的在前，
 * 年内保持各自的顺序。
 *
 * @param {object[]} records Records / 记录列表。
 * @param {(record: object) => number|string} getYear Year accessor / 取年份的函数。
 * @returns {Array<{year: string, items: object[]}>} Year buckets / 年份分组。
 */
export function groupByYear(records, getYear) {
  const buckets = new Map()
  for (const record of records) {
    const year = String(getYear(record) ?? '')
    if (!buckets.has(year)) buckets.set(year, [])
    buckets.get(year).push(record)
  }
  return [...buckets.entries()]
    .sort((a, b) => String(b[0]).localeCompare(String(a[0]), 'en', { numeric: true }))
    .map(([year, items]) => ({ year, items }))
}
